import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { JOBBOSS2_APP_ID } from "@forgepoint/shared";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useConnectionsStore } from "@/store/connectionsStore";
import {
  buildJobBoss2FinalizeInput,
  readJobBoss2CompanyDescription,
} from "@/components/erp/jobBoss2ConnectionForm";
import { toast } from "sonner";

interface JobBoss2ConnectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (connectionId: string) => void;
}

/**
 * Dialog that collects JobBOSS² API user credentials and finalizes the connection.
 * @param props - Dialog open state and callbacks
 * @returns The connection dialog
 */
export function JobBoss2ConnectionDialog({
  open,
  onOpenChange,
  onSuccess,
}: JobBoss2ConnectionDialogProps) {
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [requiresEgressProxy, setRequiresEgressProxy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const finalizeConnection = useConnectionsStore(
    (state) => state.finalizeConnection,
  );
  const fetchConnectionsByApp = useConnectionsStore(
    (state) => state.fetchConnectionsByApp,
  );
  const finalizeError = useConnectionsStore(
    (state) =>
      state.connectionStates.get(JOBBOSS2_APP_ID)?.finalizeError ?? null,
  );
  const clearConnectionError = useConnectionsStore(
    (state) => state.clearConnectionError,
  );

  useEffect(() => {
    clearConnectionError(JOBBOSS2_APP_ID);
    if (open) {
      return;
    }
    setClientId("");
    setClientSecret("");
    setRequiresEgressProxy(false);
    setError(null);
    setIsSubmitting(false);
  }, [open, clearConnectionError]);

  const handleSubmit = useCallback(async () => {
    const result = buildJobBoss2FinalizeInput({
      clientId,
      clientSecret,
      requiresEgressProxy,
    });
    if ("error" in result) {
      setError(result.error);
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      const connection = await finalizeConnection(result.input);
      if (!connection) {
        setError(
          "JobBOSS² connection validation failed. Check the client ID and client secret.",
        );
        return;
      }
      const company = readJobBoss2CompanyDescription(connection.config);
      toast.success(
        company ? `Connected to ${company}.` : "JobBOSS² connection created.",
      );
      await fetchConnectionsByApp(JOBBOSS2_APP_ID);
      onSuccess?.(connection.id);
      onOpenChange(false);
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : "Failed to create JobBOSS² connection.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    clientId,
    clientSecret,
    requiresEgressProxy,
    fetchConnectionsByApp,
    finalizeConnection,
    onOpenChange,
    onSuccess,
  ]);

  const displayedError = error ?? finalizeError;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect JobBOSS²</DialogTitle>
          <DialogDescription>
            Enter the API user credentials issued by ECI for this JobBOSS²
            company. Credentials are stored with the connection and are not
            returned after setup.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            handleSubmit();
          }}
        >
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="jobboss2-client-id">Client ID</Label>
              <Input
                id="jobboss2-client-id"
                value={clientId}
                onChange={(event) => setClientId(event.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="jobboss2-client-secret">Client Secret</Label>
              <Input
                id="jobboss2-client-secret"
                type="password"
                value={clientSecret}
                onChange={(event) => setClientSecret(event.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="flex items-center justify-between gap-4">
              <div className="grid gap-1">
                <Label htmlFor="jobboss2-egress-proxy">
                  Require static egress IP
                </Label>
                <p className="text-sm text-muted-foreground">
                  Route all requests for this connection through Forgepoint's
                  allowlistable IP.
                </p>
              </div>
              <Switch
                id="jobboss2-egress-proxy"
                checked={requiresEgressProxy}
                onCheckedChange={setRequiresEgressProxy}
              />
            </div>
            {displayedError && (
              <p className="text-sm text-destructive">{displayedError}</p>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !clientId.trim() || !clientSecret}
            >
              Connect
              {isSubmitting && (
                <Loader2 className="ml-2 h-4 w-4 animate-spin" />
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
